import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, CircularProgress } from '@mui/material'

import { axiosInstance } from '../../../service/axiosManager'
import { getGuiSessionConfig, isGuiSession } from '../../workspace/config/guiSessionConfig'
import { formatTestsForRender } from '../../workspace/utils/TestCases/testDataFormatter'
import { filteredText, replayChatEvents } from './experimentOverviewStats'
import { mergeEvents } from './useConditionTelemetry'

const API = process.env.REACT_APP_HOST_API

// The Monitor's replay, loaded when a Replay tab first opens: it brings the code editor and the
// whole replay, which the rest of the experiment page does not need.
const TelemetryReplayView = lazy(() =>
  import('../../commonUnit/StudentDetailView').then((module) => ({ default: module.TelemetryReplayView }))
)

const parsedTestCode = (row) => {
  if (row?.test_code && typeof row.test_code === 'object') return row.test_code
  try {
    return typeof row?.test_code === 'string' ? JSON.parse(row.test_code) : null
  } catch (error) {
    return null
  }
}

// Why the replay could not load, for the instructor.
const failureText = (error) => {
  const status = error?.response?.status
  if (status === 401 || status === 403) return 'This account cannot load this student’s replay. Sign in again, then try again.'
  return 'The replay could not be loaded. Try again.'
}

const Waiting = ({ children }) => (
  <Box sx={{ height: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, color: 'text.secondary', fontSize: 14 }}>
    <CircularProgress size={20} /> {children}
  </Box>
)

/**
 * A student's replay in the experiment Overview's student dialog: everything they did in one
 * condition session - their code at every edit, runs, tests and AI conversation (the Code Coach
 * chat's too, replayChatEvents) - played back by the Monitor's replay (TelemetryReplayView) with
 * that condition's task, starter and tests (a web exercise as the Monitor replays one), and the
 * student's words through the Overview's word filter. The Overview's own feed leaves out the
 * edits' code, keys and mouse, so this fetches the student's whole telemetry for the session
 * (GET /telemetry/session/:id/user/:userId) once; Refresh fetches it again.
 * The chat shows only what was sent and received - never the chat's welcome message, which
 * a condition without AI chat does not have and which would tell it apart on the Overview.
 *   sessionId: the condition session; userId: the student; row: that condition's session row
 *   (task, test_list, test_code's template, its type for a web exercise).
 */
export default function StudentReplay({ sessionId, userId, row }) {
  const key = `${sessionId}:${userId}`
  const [load, setLoad] = useState({ key, loading: true, error: null, events: [] })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!sessionId || !userId) return undefined
    const controller = new AbortController()
    // Refresh keeps what is shown until the answer comes; another student's never stays.
    setLoad((previous) => ({ key, loading: true, error: null, events: previous.key === key ? previous.events : [] }))
    axiosInstance
      .get(`${API}/telemetry/session/${sessionId}/user/${userId}`, { timeout: 60000, signal: controller.signal })
      // Without the copies a resent batch leaves (mergeEvents), in time order.
      .then((response) => setLoad({ key, loading: false, error: null, events: mergeEvents([], response.data?.events || []) }))
      .catch((error) => {
        if (controller.signal.aborted) return
        setLoad({ key, loading: false, error: failureText(error), events: [] })
      })
    return () => controller.abort()
  }, [key, sessionId, userId, attempt])

  const events = useMemo(() => replayChatEvents(load.events), [load.events])
  // Rebuilt only when what it uses changes, not at every poll's new row.
  const rowKey = JSON.stringify([row?.id, row?.type, row?.task, row?.test_list, row?.test_code])
  const { gui, guiConfig, replayOptions } = useMemo(() => {
    const shown = JSON.parse(rowKey)
    const sessionRow = { id: shown[0], type: shown[1], task: shown[2], test_list: shown[3], test_code: shown[4] }
    const web = isGuiSession(sessionRow)
    const config = web ? getGuiSessionConfig(sessionRow) : null
    const testCode = parsedTestCode(sessionRow)
    return {
      gui: web,
      guiConfig: config,
      replayOptions: {
        task: (web ? config?.assignment : null) || sessionRow.task || '',
        starterCode: (web ? config?.starterCode : null) || testCode?.template || '',
        testCases: web ? [] : formatTestsForRender(sessionRow, []),
        // The dialog's width, not a full page's: narrower side panels and terminal.
        initialSizes: { leftWidth: 300, rightWidth: 360, terminalHeight: 160 },
        showNavbar: false,
        chatWelcome: null,
        formatText: filteredText,
      },
    }
  }, [rowKey])

  if (load.loading && !load.events.length) return <Waiting>Loading the replay…</Waiting>
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <span style={{ fontSize: '13px', color: '#6b7280' }}>Everything they did in this exercise, as it happened. Play it, or drag the timeline.</span>
        <Button size="small" onClick={() => setAttempt((n) => n + 1)} disabled={load.loading}>
          {load.loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Box>
      {load.error ? (
        <Alert severity="warning">{load.error}</Alert>
      ) : (
        <Suspense fallback={<Waiting>Loading the replay…</Waiting>}>
          <TelemetryReplayView events={events} isGuiSession={gui} guiConfig={guiConfig} replayOptions={replayOptions} />
        </Suspense>
      )}
    </>
  )
}
